import { useState, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { motion } from 'motion/react';
import { Loader2, Send, Upload, Image as ImageIcon, Video, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@primitive/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@primitive/alert-dialog';
import { Button } from '@primitive/button';
import { Label } from '@primitive/label';
import { Textarea } from '@primitive/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@primitive/select';
import { Input } from '@primitive/input';
import { useAuthStore } from '@stores/useAuthStore';
import { useUIStore } from '@stores/useUIStore';
import { useUserProfile } from '@hooks/useUserProfile';
import {
  FeedbackCategory,
  FEEDBACK_MEDIA_IMAGE_MAX_BYTES,
  FEEDBACK_MEDIA_MAX_FILES,
  FEEDBACK_MEDIA_VIDEO_MAX_BYTES,
} from "@bbb/model";
import { MediaFile, FeedbackButtonProps, ApiClientError } from "@interfaces";
import { submitFeedback } from "@lib/apiClients";

export function FeedbackButton({
  onLoginClick,
  onSignupClick,
  hideFloatingButton = false,
  isOpen,
  onOpenChange,
}: FeedbackButtonProps) {
  const { isAuthenticated } = useAuthStore(useShallow(s => ({
    isAuthenticated: s.isAuthenticated,
  })));
  const { data: user } = useUserProfile({
    enabled: isAuthenticated,
  });
  const { feedbackPrefillMessage, setFeedbackPrefillMessage } = useUIStore(useShallow(s => ({
    feedbackPrefillMessage: s.feedbackPrefillMessage,
    setFeedbackPrefillMessage: s.setFeedbackPrefillMessage,
  })));

  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>('bug');
  const [message, setMessage] = useState('');
  const [projectName, setProjectName] = useState('');
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mediaFilesRef = useRef<MediaFile[]>([]);

  // is a prefilled message provided?
  useEffect(() => {
    if (isOpen && feedbackPrefillMessage) {
      setMessage(feedbackPrefillMessage);
      setFeedbackPrefillMessage('');
    };
  }, [isOpen, feedbackPrefillMessage, setFeedbackPrefillMessage]);

  useEffect(() => {
    mediaFilesRef.current = mediaFiles;
  }, [mediaFiles]);

  useEffect(() => {
    if (isOpen) return;
    setMediaFiles((prev) => {
      prev.forEach((media) => URL.revokeObjectURL(media.preview));
      return [];
    });
  }, [isOpen]);

  useEffect(() => {
    return () => {
      mediaFilesRef.current.forEach((media) => URL.revokeObjectURL(media.preview));
    };
  }, []);

  const handleButtonClick = () => {
    if (!isAuthenticated) {
      setShowAuthDialog(true);
    } else {
      onOpenChange?.(true);
    }
  };

  const handleMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isSubmitting) return;

    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const hasExistingVideo = mediaFiles.some(m => m.type === 'video');
    const currentImages = mediaFiles.filter(m => m.type === 'image').length;
    const incomingImages = files.filter(f => f.type.startsWith('image/'));
    const incomingVideos = files.filter(f => f.type.startsWith('video/'));

    if (incomingImages.length > 0 && incomingVideos.length > 0) {
      toast.error('Please upload either images or a single video, not both.');
      e.target.value = '';
      return;
    }

    if (hasExistingVideo) {
      toast.error('Remove the existing video before uploading more media.');
      e.target.value = '';
      return;
    }

    if (incomingVideos.length > 0) {
      if (incomingVideos.length > 1 || mediaFiles.length > 0) {
        toast.error('You can only upload one video per submission.');
        e.target.value = '';
        return;
      }
    }

    if (incomingImages.length > 0) {
      if (currentImages + incomingImages.length > FEEDBACK_MEDIA_MAX_FILES) {
        toast.error(`You can only upload up to ${FEEDBACK_MEDIA_MAX_FILES} images.`);
        e.target.value = '';
        return;
      }
    }

    const validFiles: MediaFile[] = [];

    for (const file of files) {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');

      if (!isImage && !isVideo) {
        toast.error(`${file.name} is not a valid image or video file.`);
        continue;
      }

      if (isVideo && file.size > FEEDBACK_MEDIA_VIDEO_MAX_BYTES) {
        toast.error(`${file.name} exceeds the 30MB limit for videos.`);
        continue;
      }

      let fileForUpload = file;
      if (isImage && file.size > FEEDBACK_MEDIA_IMAGE_MAX_BYTES) {
        let compressed: File | null = null;
        try {
          compressed = await compressFeedbackImage(file, FEEDBACK_MEDIA_IMAGE_MAX_BYTES);
        } catch {
          compressed = null;
        }
        if (!compressed || compressed.size > FEEDBACK_MEDIA_IMAGE_MAX_BYTES) {
          toast.error(`${file.name} exceeds the 5MB limit for images.`);
          continue;
        }

        fileForUpload = compressed;
        if (compressed.size < file.size) {
          toast.info(`${file.name} was compressed before upload.`);
        }
      }

      const preview = URL.createObjectURL(fileForUpload);
      validFiles.push({
        id: `${Date.now()}-${Math.random()}`,
        file: fileForUpload,
        preview,
        type: isImage ? 'image' : 'video',
      });
    }

    setMediaFiles(prev => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const removeMediaFile = (id: string) => {
    if (isSubmitting) return;

    setMediaFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== id);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (!isAuthenticated || !user) {
      toast.error('You must be logged in to submit feedback');
      onOpenChange?.(false);
      setShowAuthDialog(true);
      return;
    }

    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    if (category === 'report-community' && !projectName.trim()) {
      toast.error('Please enter the community name you want to report');
      return;
    }

    setIsSubmitting(true);

    const submissionResponse = await submitFeedback({
      category,
      message: message.trim(),
      projectName: category === 'report-community' ? projectName.trim() : undefined,
      mediaFiles: mediaFiles.map(media => media.file),
    }).catch((error) => {
      const description =
        error instanceof ApiClientError
          ? error.message
          : 'Would you like to reach out to us on Telegram? https://t.me/gilaquest. we are all ears!';
      toast.error('Oh no! Something went wrong!', { description });
      return null;
    });
    if (!submissionResponse) {
      setIsSubmitting(false);
      return;
    }

    // Show success message
    if (category === 'report-community') {
      toast.success('Report Submitted', {
        description: 'Thank you for helping keep our platform safe. We will review this community.',
      });
    } else {
      toast.success('Feedback Submitted', {
        description: 'Thank you for your feedback! We will review it.',
      });
    }

    // Clean up media previews
    mediaFiles.forEach(media => URL.revokeObjectURL(media.preview));

    // Reset form
    setMessage('');
    setProjectName('');
    setCategory('bug');
    setMediaFiles([]);
    setIsSubmitting(false);
    onOpenChange?.(false);
  };

  const getCategoryLabel = (cat: FeedbackCategory) => {
    switch (cat) {
      case 'bug':
        return '🐛 Bug Report';
      case 'feature':
        return '✨ Feature Request';
      case 'improvement':
        return '💡 Improvement Suggestion';
      case 'report-community':
        return '🚨 Report Suspicious Project';
      case 'other':
        return '💬 Other Feedback';
      default:
        return 'Select Category';
    }
  };

  return (
    <>
      {!hideFloatingButton && (
        <motion.button
          onClick={handleButtonClick}
          aria-label="Open feedback form"
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 320, damping: 22 }}
          className="fixed bottom-1 right-0 z-50 origin-right rounded-r-none rounded-l-full focus:outline-none group"
        >
          <div className="flex h-28 w-9 items-center justify-end rounded-r-none rounded-l-full pr-1.5 transition-[width,box-shadow] duration-150 ease-out group-hover:w-11 group-hover:shadow-xl group-focus-visible:w-11 group-focus-visible:shadow-xl">
            <span className="whitespace-nowrap text-sm font-semibold tracking-normal [writing-mode:vertical-rl] rotate-180">
              Feedback
            </span>
          </div>
        </motion.button>
      )}

      {/* Authentication Required Dialog */}
      <AlertDialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign In Required</AlertDialogTitle>
            <AlertDialogDescription>
              You need to be signed in with your wallet to submit feedback or report projects.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowAuthDialog(false);
                onLoginClick?.();
              }}
            >
              Log In
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => {
                setShowAuthDialog(false);
                onSignupClick?.();
              }}
              className="bg-primary hover:bg-primary/90"
            >
              Sign Up
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Feedback Modal */}
      <Dialog open={isOpen} onOpenChange={isSubmitting ? undefined : onOpenChange}>
        <DialogContent className="w-full max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Submit Feedback</DialogTitle>
            <DialogDescription>
              Help us improve Gila Quest or report suspicious projects
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            {/* Category Selection */}
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={category}
                onValueChange={(value) => setCategory(value as FeedbackCategory)}
                disabled={isSubmitting}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bug">{getCategoryLabel('bug')}</SelectItem>
                  <SelectItem value="feature">{getCategoryLabel('feature')}</SelectItem>
                  <SelectItem value="improvement">{getCategoryLabel('improvement')}</SelectItem>
                  <SelectItem value="report-community">{getCategoryLabel('report-community')}</SelectItem>
                  <SelectItem value="other">{getCategoryLabel('other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Project Name Input (only for report-community) */}
            {category === 'report-community' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2"
              >
                <Label htmlFor="projectName">Project Name</Label>
                <Input
                  id="projectName"
                  placeholder="Enter the name of the suspicious community"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  disabled={isSubmitting}
                  required={category === 'report-community'}
                />
              </motion.div>
            )}

            {/* Message Input */}
            <div className="space-y-2">
              <Label htmlFor="message">
                {category === 'report-community' ? 'Why are you reporting this community?' : 'Your Message'}
              </Label>
              <Textarea
                id="message"
                placeholder={
                  category === 'report-community'
                    ? 'Please describe why you believe this community is suspicious...'
                    : category === 'bug'
                      ? 'Describe the bug you encountered...'
                      : category === 'feature'
                        ? 'Describe the feature you would like to see...'
                        : category === 'improvement'
                          ? 'How can we improve the platform?'
                          : 'Share your thoughts with us...'
                }
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isSubmitting}
                className="min-w-1 min-h-[200px] max-h-60  overflow-y-auto overflow-x-auto break-words whitespace-pre-wrap"
                required
              />
            </div>

            {/* Media Upload */}
            <div className="space-y-2">
              <Label>Attachments (Optional)</Label>
              <div className="space-y-3">
                {/* Upload Button */}
                <div className="flex items-center gap-2">
                  <Input
                    id="media-upload"
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleMediaUpload}
                    disabled={isSubmitting}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('media-upload')?.click()}
                    disabled={
                      isSubmitting ||
                      mediaFiles.some(m => m.type === 'video') ||
                      mediaFiles.filter(m => m.type === 'image').length >= FEEDBACK_MEDIA_MAX_FILES
                    }
                    className="gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Upload Media
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Up to 4 images (max 5MB each) or 1 video (max 30MB)
                  </p>
                </div>

                {/* Media Previews */}
                {mediaFiles.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {mediaFiles.map((media) => (
                      <div
                        key={media.id}
                        className="relative group rounded-lg overflow-hidden border bg-muted"
                      >
                        {media.type === 'image' ? (
                          <img
                            src={media.preview}
                            alt="Preview"
                            className="w-full h-24 object-cover"
                          />
                        ) : (
                          <div className="w-full h-24 flex flex-col items-center justify-center gap-1 bg-muted">
                            <Video className="h-6 w-6 text-muted-foreground" />
                            <p className="text-xs text-muted-foreground truncate px-2 max-w-full">
                              {media.file.name}
                            </p>
                          </div>
                        )}

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={() => removeMediaFile(media.id)}
                          disabled={isSubmitting}
                          className={`absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 transition-opacity ${isSubmitting
                            ? 'opacity-40 cursor-not-allowed'
                            : 'opacity-0 group-hover:opacity-100'
                            }`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>

                        {/* File Type Indicator */}
                        <div className="absolute bottom-1 left-1 bg-background/80 backdrop-blur-sm rounded px-1.5 py-0.5">
                          {media.type === 'image' ? (
                            <ImageIcon className="h-3 w-3" />
                          ) : (
                            <Video className="h-3 w-3" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (isSubmitting) return;
                  onOpenChange?.(false);
                  setMessage('');
                  setProjectName('');
                  mediaFiles.forEach(media => URL.revokeObjectURL(media.preview));
                  setMediaFiles([]);
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" className="gap-2" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

async function compressFeedbackImage(file: File, maxBytes: number): Promise<File | null> {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') {
    return null;
  }

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const src = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(src);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(src);
      reject(new Error('Failed to read image'));
    };
    img.src = src;
  });

  const canvas = document.createElement('canvas');
  const maxDimension = 2560;
  const longestSide = Math.max(image.width, image.height);
  const scale = longestSide > maxDimension ? maxDimension / longestSide : 1;
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }

  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  const targetType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  let quality = 0.9;
  let blob: Blob | null = null;

  while (quality >= 0.5) {
    blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((result) => resolve(result), targetType, quality),
    );

    if (blob && blob.size <= maxBytes) {
      break;
    }

    quality -= 0.1;
  }

  if (!blob) {
    return null;
  }

  const extension = targetType === 'image/png' ? 'png' : 'jpg';
  const safeName = file.name.replace(/\.[^.]+$/, '');
  return new File([blob], `${safeName}.${extension}`, {
    type: targetType,
    lastModified: Date.now(),
  });
}
